import { useStore, type Identifier } from "ra-core";
import { useCallback } from "react";
import type { DealHistoryEvent, DealHistoryEventType } from "../types";

export const DEAL_HISTORY_STORE_KEY = "crm.deal_history_map";

export type DealHistoryMap = Record<string, DealHistoryEvent[]>;

/** Formata texto de diferença de valores seguindo o padrão solicitado */
export const formatDiffText = (oldVal: any, newVal: any): string => {
  const isOldEmpty =
    oldVal === undefined ||
    oldVal === null ||
    (typeof oldVal === "string" && oldVal.trim() === "") ||
    (Array.isArray(oldVal) && oldVal.length === 0);

  const isNewEmpty =
    newVal === undefined ||
    newVal === null ||
    (typeof newVal === "string" && newVal.trim() === "") ||
    (Array.isArray(newVal) && newVal.length === 0);

  const formatItem = (v: any) => {
    if (v === null || v === undefined) return "vazio";
    if (typeof v === "number") {
      return v.toLocaleString("pt-BR");
    }
    if (typeof v === "boolean") {
      return v ? "Sim" : "Não";
    }
    return String(v);
  };

  if (isOldEmpty && !isNewEmpty) {
    return `saiu de vazio para "${formatItem(newVal)}"`;
  }
  if (!isOldEmpty && isNewEmpty) {
    return `saiu de "${formatItem(oldVal)}" para vazio`;
  }
  return `de "${formatItem(oldVal)}" para "${formatItem(newVal)}"`;
};

// Funções utilitárias diretas para salvar no localStorage quando fora do contexto de hook
export const appendDealHistoryDirect = (
  dealId: Identifier,
  event: Omit<DealHistoryEvent, "id" | "deal_id" | "created_at">,
) => {
  try {
    const raw = localStorage.getItem(DEAL_HISTORY_STORE_KEY);
    const map: DealHistoryMap = raw ? JSON.parse(raw) : {};
    const key = String(dealId);
    const existing = map[key] || [];

    const newEvent: DealHistoryEvent = {
      ...event,
      id:
        "hist_" +
        Date.now().toString(36) +
        "_" +
        Math.random().toString(36).substring(2, 6),
      deal_id: dealId,
      created_at: new Date().toISOString(),
    };

    map[key] = [newEvent, ...existing];
    localStorage.setItem(DEAL_HISTORY_STORE_KEY, JSON.stringify(map));

    // Dispara evento para sincronizar componentes escutando mudanças
    window.dispatchEvent(
      new CustomEvent("crm:deal_history_updated", { detail: { dealId } }),
    );
    return newEvent;
  } catch (e) {
    console.error("Erro ao registrar histórico do negócio:", e);
    return null;
  }
};

export const useDealHistory = (dealId?: Identifier) => {
  const [historyMap, setHistoryMap] = useStore<DealHistoryMap>(
    DEAL_HISTORY_STORE_KEY,
    {},
  );

  const getHistory = useCallback(
    (id?: Identifier): DealHistoryEvent[] => {
      const targetId = id || dealId;
      if (!targetId) return [];
      return (historyMap || {})[String(targetId)] || [];
    },
    [dealId, historyMap],
  );

  const addEvent = useCallback(
    (
      targetDealId: Identifier,
      eventData: {
        type: DealHistoryEventType;
        label: string;
        description: string;
        field_name?: string;
        old_value?: any;
        new_value?: any;
        author_name?: string;
      },
    ) => {
      const key = String(targetDealId);
      const existing = (historyMap || {})[key] || [];

      const newEvent: DealHistoryEvent = {
        ...eventData,
        id:
          "hist_" +
          Date.now().toString(36) +
          "_" +
          Math.random().toString(36).substring(2, 6),
        deal_id: targetDealId,
        created_at: new Date().toISOString(),
      };

      const updated = {
        ...(historyMap || {}),
        [key]: [newEvent, ...existing],
      };

      setHistoryMap(updated);
      try {
        localStorage.setItem(DEAL_HISTORY_STORE_KEY, JSON.stringify(updated));
      } catch {
        void 0;
      }

      window.dispatchEvent(
        new CustomEvent("crm:deal_history_updated", {
          detail: { dealId: targetDealId },
        }),
      );
      return newEvent;
    },
    [historyMap, setHistoryMap],
  );

  const recordStageChange = useCallback(
    (
      targetDealId: Identifier,
      oldStage: string,
      newStage: string,
      stageLabels?: Record<string, string>,
    ) => {
      const oldLabel = stageLabels?.[oldStage] || oldStage;
      const newLabel = stageLabels?.[newStage] || newStage;
      if (oldStage === newStage) return;

      return addEvent(targetDealId, {
        type: "stage_change",
        label: "Etapa alterada",
        description: `etapa: ${oldLabel} → ${newLabel}`,
        old_value: oldLabel,
        new_value: newLabel,
      });
    },
    [addEvent],
  );

  const recordFunnelChange = useCallback(
    (
      targetDealId: Identifier,
      oldFunilId: string | undefined,
      newFunilId: string,
      funnelLabels?: Record<string, string>,
    ) => {
      const oldLabel = oldFunilId
        ? funnelLabels?.[oldFunilId] || oldFunilId
        : "Padrão";
      const newLabel = funnelLabels?.[newFunilId] || newFunilId;
      if (oldFunilId === newFunilId) return;

      return addEvent(targetDealId, {
        type: "funnel_change",
        label: "Funil alterado",
        description: `funil: ${oldLabel} → ${newLabel}`,
        old_value: oldLabel,
        new_value: newLabel,
      });
    },
    [addEvent],
  );

  const recordFieldChange = useCallback(
    (
      targetDealId: Identifier,
      fieldName: string,
      oldVal: any,
      newVal: any,
      isCustom = false,
    ) => {
      if (oldVal === newVal) return;
      const diffText = formatDiffText(oldVal, newVal);

      return addEvent(targetDealId, {
        type: isCustom ? "custom_field_change" : "field_change",
        label: `Campo "${fieldName}" alterado`,
        description: `campo: ${fieldName} ${diffText}`,
        field_name: fieldName,
        old_value: oldVal,
        new_value: newVal,
      });
    },
    [addEvent],
  );

  const recordActivityCreated = useCallback(
    (
      targetDealId: Identifier,
      activityTitle: string,
      activityType: string,
      dateStr?: string,
    ) => {
      const dateSuffix = dateStr ? ` para ${dateStr}` : "";
      return addEvent(targetDealId, {
        type: "activity_created",
        label: "Atividade criada",
        description: `atividade criada: "${activityTitle}" (${activityType})${dateSuffix}`,
        new_value: activityTitle,
      });
    },
    [addEvent],
  );

  const recordOwnerChange = useCallback(
    (targetDealId: Identifier, oldOwner: string, newOwner: string) => {
      return addEvent(targetDealId, {
        type: "owner_change",
        label: "Proprietário alterado",
        description: `proprietário alterado: ${formatDiffText(oldOwner, newOwner)}`,
        old_value: oldOwner,
        new_value: newOwner,
      });
    },
    [addEvent],
  );

  const recordStatusChange = useCallback(
    (targetDealId: Identifier, oldStatus: string, newStatus: string) => {
      if (oldStatus === newStatus) return;
      return addEvent(targetDealId, {
        type: "status_change",
        label: "Status do negócio alterado",
        description: `status: de "${oldStatus}" para "${newStatus}"`,
        old_value: oldStatus,
        new_value: newStatus,
      });
    },
    [addEvent],
  );

  return {
    history: dealId ? getHistory(dealId) : [],
    getHistory,
    addEvent,
    recordStageChange,
    recordFunnelChange,
    recordFieldChange,
    recordActivityCreated,
    recordOwnerChange,
    recordStatusChange,
  };
};
